import React from 'react';

import { Box, Button, Typography } from '@mui/material';
import { isNotNil } from 'es-toolkit';

import { DateFormatPatterns } from '@clearverify/constants';
import { formatDate, getDateTimeInTimezone } from '@clearverify/utils';

import { ModalId } from 'src/constants/modals';
import { useApiVerificationRequest } from 'src/hooks/apiVerificationRequest';
import { useGeneratedReports } from 'src/hooks/generatedReport';
import { useInboxItemParamId } from 'src/hooks/inboxItem';
import { useModal } from 'src/hooks/shared';
import { useCurrentUser } from 'src/hooks/user';
import { ViewApiVerificationRequestReportModalArgs } from 'src/modals/verifier';

import {
  requestBodyContainerStyles,
  viewReportButtonStyles,
} from '../InboxItemContentVerificationRequest/shared/styles';

export const InboxItemContentApiVerificationRequest: React.FC = () => {
  const { openModal } = useModal();

  const { timezone } = useCurrentUser();

  const inboxItemId = useInboxItemParamId();

  const { apiVerificationRequest } = useApiVerificationRequest({ inboxItemId });
  const { generatedReportsMeta, loading } = useGeneratedReports({ inboxItemId });

  const reportIsGenerated = React.useMemo(
    () => generatedReportsMeta.some(isNotNil),
    [generatedReportsMeta],
  );

  const createdAtFormatted = React.useMemo(
    () =>
      formatDate(
        getDateTimeInTimezone(apiVerificationRequest?.createdAt, timezone)?.toISO(),
        DateFormatPatterns.meridiemTimePaddedWithDate,
      ),
    [apiVerificationRequest?.createdAt, timezone],
  );

  const handleViewReportClick = React.useCallback(() => {
    openModal(ModalId.ViewApiVerificationRequestReportModal, {
      inboxItemId,
      reportType: apiVerificationRequest?.type ?? null,
    } satisfies ViewApiVerificationRequestReportModalArgs);
  }, [apiVerificationRequest?.type, inboxItemId, openModal]);

  return (
    <Box sx={requestBodyContainerStyles}>
      <Typography variant="h6" mb={1}>
        Verification process completed successfully
      </Typography>
      <Typography variant="body2">
        The latest verification information was successfully delivered to our integration partner at{' '}
        {createdAtFormatted}.
      </Typography>
      <Box display="flex" justifyContent="flex-end" mt={2.5}>
        <Button
          variant="contained"
          disabled={!reportIsGenerated}
          loading={loading}
          sx={viewReportButtonStyles}
          onClick={handleViewReportClick}
        >
          View Data
        </Button>
      </Box>
    </Box>
  );
};
